import { useMemo } from "react";

import { ProgressAnimationComponent } from "@mirage/mosaics/ConversationAssistant/components/ProgressAnimationComponent";
import useConversation from "@mirage/service-conversation/hooks/useConversation";
import {
  ProgressUpdateStep,
  type Conversation,
  type UserMessageOptions,
} from "@mirage/service-conversation/types";
import { usePrevious } from "@mirage/shared/util/hooks";

/**
 * Returns the progress component and isWaitingForResponse flag for the conversation.
 * If the conversationId is not provided, it will return undefined progressComponent and true when messages are loading.
 * @param conversationId - The id of the conversation.
 * @param userMessageOptions - The user message options.
 * @returns The progress component and isWaitingForResponse flag.
 */
export function useConversationProgress(
  conversationId?: string,
  userMessageOptions?: UserMessageOptions,
) {
  const conversation = useConversation(conversationId);

  const isWaitingForResponse = useMemo(() => {
    const lastMessage =
      conversation?.messages?.[conversation?.messages.length - 1];
    return conversation?.messages
      ? !!conversation?.messages?.some(
          (message) => message.status === "pending",
        ) || lastMessage?.status === "streaming"
      : true; // true when loading
  }, [conversation?.messages]);

  // Extract sources from progress updates context
  const progressSources = useMemo(() => {
    if (!conversation?.progressUpdates?.length) return [];
    const latestUpdate =
      conversation.progressUpdates[conversation.progressUpdates.length - 1];
    const contextSources = latestUpdate?.context || [];
    return contextSources;
  }, [conversation?.progressUpdates]);

  const isGeneratingResponse = useMemo(() => {
    if (!conversation?.progressUpdates?.length) return false;

    const lastUpdate =
      conversation.progressUpdates[conversation.progressUpdates.length - 1];
    return (
      lastUpdate?.step ===
        ProgressUpdateStep.STEP_RESPONSE_GENERATION_FROM_LLM ||
      lastUpdate?.step ===
        ProgressUpdateStep.STEP_RESPONSE_GENERATION_FROM_AGENT
    );
  }, [conversation?.progressUpdates]);

  const progressComponent = useMemo(() => {
    if (isWaitingForResponse || conversation?.progressUpdates?.length) {
      const lastUpdate =
        conversation?.progressUpdates?.[
          conversation?.progressUpdates.length - 1
        ];

      return (
        <ProgressAnimationComponent
          isWaitingForResponse={isWaitingForResponse}
          progressStep={lastUpdate?.step}
          userMessageOptions={userMessageOptions}
          sources={progressSources}
          conversation={conversation || ({} as Conversation)}
        />
      );
    }
    return undefined;
  }, [conversation, isWaitingForResponse, userMessageOptions, progressSources]);

  // Show the skeleton when the progress step is STEP_SUGGESTED_QUERIES_GENERATION
  // and we were already generating follow up suggestions or generating a response from the LLM
  // This is to avoid a flicker of the last step on subsequent queries
  const currentProgressStep = useMemo(() => {
    if (!conversation?.progressUpdates?.length) return undefined;
    const lastUpdate =
      conversation.progressUpdates[conversation.progressUpdates.length - 1];
    return isWaitingForResponse
      ? lastUpdate?.step
      : ProgressUpdateStep.STEP_SEARCH;
  }, [conversation?.progressUpdates, isWaitingForResponse]);
  const previousProgressStep = usePrevious(currentProgressStep);
  const showFollowUpSuggestionsSkeleton =
    currentProgressStep ===
      ProgressUpdateStep.STEP_SUGGESTED_QUERIES_GENERATION &&
    (previousProgressStep ===
      ProgressUpdateStep.STEP_SUGGESTED_QUERIES_GENERATION ||
      previousProgressStep ===
        ProgressUpdateStep.STEP_RESPONSE_GENERATION_FROM_LLM);

  return {
    progressComponent,
    isWaitingForResponse,
    isGeneratingResponse,
    showFollowUpSuggestionsSkeleton,
  };
}
